// Client mirror of the server's operation-profile validation
// (internal/profile/validate_queuing.go, validate_work.go): same codes, same paths,
// same order. SERVICE_UNKNOWN and PRIMARY_UNUSED need facts only the server has.
import { t } from './staffText'
import type {
  CallRule,
  OperationDoc,
  ProfileProblem,
  ProfileQueue,
  ProfileSegment,
  ProfileWorkProfile,
  RuleCondition,
  RuleConditionKind,
} from './types'

export interface Checked { problems: ProfileProblem[]; warnings: ProfileProblem[] }

export const LIMITS = { groups: 50, queues: 100, segmentsPerGroup: 50, rules: 10, conditions: 5 } as const

export type ProblemCode =
  | 'SERVICE_IN_TWO_GROUPS' | 'SERVICE_UNKNOWN' | 'ID_MISSING' | 'ID_TAKEN' | 'QUEUE_UNKNOWN'
  | 'SEGMENT_SERVICE_OUTSIDE' | 'LATE_NEEDS_APPOINTMENT' | 'LATE_RANGE' | 'APPOINTMENT_QUEUE_DEFAULT'
  | 'APPOINTMENT_QUEUE_ROUTE' | 'NAME_MISSING' | 'NAME_TAKEN' | 'CODE_FORMAT' | 'CODE_TAKEN' | 'LETTER_FORMAT'
  | 'RANGE_BOUNDS' | 'RANGE_SHORT' | 'RANGES_OVERLAP' | 'TARGET_RANGE' | 'TOO_MANY' | 'VALUE_UNKNOWN' | 'QUEUE_UNUSED'
  | 'RULES_COUNT' | 'FINALLY_CONDITIONS' | 'FINALLY_QUEUES' | 'CONDITIONS_COUNT' | 'RULE_OUTSIDE_PROFILE'
  | 'RULE_EMPTY' | 'SINGLE_NEEDS_ONE' | 'RULE_ID_TAKEN' | 'SHARE_RANGE' | 'SHARES_NOT_FAIR' | 'MINUTES_RANGE'
  | 'COUNT_RANGE' | 'OP_MISSING' | 'SCOPE_MISSING' | 'TIMES_EQUAL' | 'CONDITION_QUEUE_OUTSIDE'
  | 'PROFILE_QUEUE_UNKNOWN' | 'POOL_UNKNOWN' | 'TIMER_RANGE' | 'EARLY_RANGE' | 'MIXED_CONDITIONS' | 'PRIMARY_UNUSED'
  | 'NAME_TOO_LONG' | 'LISTED_TWICE' | 'PROFILE_EMPTY' | 'REPEAT_RANGE' | 'SHARE_QUEUE_OUTSIDE' | 'TIME_RANGE'

type Args = Record<string, string | number>

const QUEUE_CODE = /^[A-Z0-9_-]{1,16}$/
const LETTER = /^[A-Z]{1,4}$/
const LANGUAGE = /^[A-Za-z]{2,3}(-[A-Za-z]{2})?$/
const PRIORITIES = new Set(['standard', 'priority', 'vip'])
const SOURCES = new Set(['kiosk', 'reception', 'counter', 'mobile', 'appointment'])
const POOLS = new Set(['desk', 'branch', 'staff'])
const ORDERS = new Set(['single', 'defined', 'max_waiting', 'max_lifetime', 'service_level', 'do_not_call', 'priority_then_waiting', 'fair_share', 'closest_to_target'])
const PRIMARY = new Set(['', 'ignore', 'with', 'without', 'both'])
const KINDS = new Set<string>(['wait_over', 'over_service_level', 'time_between', 'visits_count', 'called_count', 'caller_is_primary', 'no_primary', 'appointment_late'])
// Only called_count counts as "counting" for the mixing warning (validate_work.go).
const COUNTING = new Set<RuleConditionKind>(['called_count'])
const WAITING = new Set<RuleConditionKind>(['wait_over', 'over_service_level', 'appointment_late'])
const OPS = new Set(['lt', 'gt', 'eq'])
const SCOPES = new Set(['branch', 'profile', 'user'])

const MAX_LATE = 240
const MIN_TARGET = 60, MAX_TARGET = 14400
const MIN_NUMBER = 1, MAX_NUMBER = 9999
const MIN_SPAN = 9
const MAX_TIMER = 3600
const REPEAT_MIN = 10, REPEAT_MAX = 600
const EARLY_MAX = 14400
const MAX_MINUTES = 240, MAX_COUNT = 999, LAST_MINUTE = 1439
const MAX_WORK_NAME = 64
const MIN_SHARE = 1, MAX_SHARE = 99

const whole = (n: unknown, lo: number, hi: number) => Number.isInteger(n) && (n as number) >= lo && (n as number) <= hi

class Problems {
  list: ProfileProblem[] = []
  add(path: string, code: ProblemCode, args?: Args, warning?: boolean) {
    const p: ProfileProblem = { path, code, message: t(`profiles.problem.${code}`, 'en', args) }
    if (args) p.args = args
    if (warning) p.warning = true
    this.list.push(p)
  }
  unknown(path: string, value: string) { this.add(path, 'VALUE_UNKNOWN', { value }) }
  name(path: string, name: string | undefined, taken: Set<string>) {
    const n = (name ?? '').trim()
    const key = n.toLowerCase()
    if (n === '') this.add(path, 'NAME_MISSING')
    else if (taken.has(key)) this.add(path, 'NAME_TAKEN', { name: n })
    else taken.add(key)
  }
}

export function validateOperationDoc(doc: OperationDoc): Checked {
  const ps = new Problems()
  queuing(doc, ps)
  work(doc, ps)
  const all = ps.list.map((p, i) => [p, i] as const)
  all.sort(([a, i], [b, j]) => (pathLess(a.path, b.path) ? -1 : pathLess(b.path, a.path) ? 1 : i - j))
  const sorted = all.map(([p]) => p)
  return { problems: sorted.filter((p) => !p.warning), warnings: sorted.filter((p) => p.warning) }
}

function queuing(doc: OperationDoc, ps: Problems) {
  const queues = queueMap(doc)
  ids(doc, ps)

  if (doc.groups.length > LIMITS.groups) ps.add('groups', 'TOO_MANY', { max: LIMITS.groups })
  if (doc.queues.length > LIMITS.queues) ps.add('queues', 'TOO_MANY', { max: LIMITS.queues })

  const groupOf = new Map<string, string>()
  const groupNames = new Set<string>()
  doc.groups.forEach((g, i) => {
    const at = `groups[${i}]`
    ps.name(`${at}.name`, g.name, groupNames)

    const mine = new Set<string>()
    ;(g.serviceIds ?? []).forEach((s, k) => {
      const other = groupOf.get(s)
      if (other !== undefined) ps.add(`${at}.serviceIds[${k}]`, 'SERVICE_IN_TWO_GROUPS', { service: s, group: other })
      else groupOf.set(s, g.name)
      mine.add(s)
    })

    target(ps, `${at}.defaultQueueId`, g.defaultQueueId, queues, 'APPOINTMENT_QUEUE_DEFAULT')

    const segments = g.segments ?? []
    if (segments.length > LIMITS.segmentsPerGroup) ps.add(`${at}.segments`, 'TOO_MANY', { max: LIMITS.segmentsPerGroup })
    const segNames = new Set<string>()
    segments.forEach((s, j) => segment(ps, `${at}.segments[${j}]`, s, mine, queues, segNames))
  })

  const codes = new Set<string>()
  doc.queues.forEach((q, i) => queue(ps, `queues[${i}]`, q, codes))
  overlaps(ps, doc.queues)

  const used = queueUsed(doc)
  doc.queues.forEach((q, i) => {
    if (!used.has(q.id)) ps.add(`queues[${i}]`, 'QUEUE_UNUSED', undefined, true)
  })
}

function queueMap(doc: OperationDoc): Map<string, ProfileQueue> {
  const m = new Map<string, ProfileQueue>()
  for (const q of doc.queues) if (q.id && !m.has(q.id)) m.set(q.id, q)
  return m
}

function queueUsed(doc: OperationDoc): Set<string> {
  const used = new Set<string>()
  for (const g of doc.groups) {
    used.add(g.defaultQueueId)
    for (const s of g.segments ?? []) used.add(s.queueId)
  }
  for (const w of doc.workProfiles) for (const q of w.queueIds ?? []) used.add(q)
  used.delete('')
  return used
}

function ids(doc: OperationDoc, ps: Problems) {
  const seen = new Set<string>()
  const check = (path: string, id: string) => {
    if (!id) ps.add(path, 'ID_MISSING')
    else if (seen.has(id)) ps.add(path, 'ID_TAKEN', { id })
    else seen.add(id)
  }
  doc.groups.forEach((g, i) => {
    check(`groups[${i}].id`, g.id)
    ;(g.segments ?? []).forEach((s, j) => check(`groups[${i}].segments[${j}].id`, s.id))
  })
  doc.queues.forEach((q, i) => check(`queues[${i}].id`, q.id))
  doc.workProfiles.forEach((w, i) => check(`workProfiles[${i}].id`, w.id))
}

function target(ps: Problems, path: string, id: string, queues: Map<string, ProfileQueue>, appointmentCode?: ProblemCode) {
  const q = queues.get(id)
  if (!q) { ps.add(path, 'QUEUE_UNKNOWN'); return }
  if (appointmentCode && q.type === 'appointment') ps.add(path, appointmentCode)
}

function segment(ps: Problems, at: string, s: ProfileSegment, groupServices: Set<string>, queues: Map<string, ProfileQueue>, names: Set<string>) {
  ps.name(`${at}.name`, s.name, names)
  const appointment = s.appointment ?? ''
  target(ps, `${at}.queueId`, s.queueId, queues, appointment === 'yes' ? undefined : 'APPOINTMENT_QUEUE_ROUTE')

  ;(s.serviceIds ?? []).forEach((id, k) => {
    if (!groupServices.has(id)) ps.add(`${at}.serviceIds[${k}]`, 'SEGMENT_SERVICE_OUTSIDE', { service: id })
  })

  if (s.lateOverMin !== undefined && s.lateOverMin !== null) {
    const p = `${at}.lateOverMin`
    if (s.lateOverMin < 0 || s.lateOverMin > MAX_LATE) ps.add(p, 'LATE_RANGE')
    if (appointment !== 'yes') ps.add(p, 'LATE_NEEDS_APPOINTMENT')
  }

  if (appointment !== '' && appointment !== 'yes' && appointment !== 'no') ps.unknown(`${at}.appointment`, appointment)
  ;(s.priorities ?? []).forEach((v, k) => { if (!PRIORITIES.has(v)) ps.unknown(`${at}.priorities[${k}]`, v) })
  ;(s.sources ?? []).forEach((v, k) => { if (!SOURCES.has(v)) ps.unknown(`${at}.sources[${k}]`, v) })
  ;(s.languages ?? []).forEach((v, k) => { if (!LANGUAGE.test(v)) ps.unknown(`${at}.languages[${k}]`, v) })
}

function queue(ps: Problems, at: string, q: ProfileQueue, codes: Set<string>) {
  const key = q.code.toUpperCase()
  if (!QUEUE_CODE.test(q.code)) ps.add(`${at}.code`, 'CODE_FORMAT')
  else if (codes.has(key)) ps.add(`${at}.code`, 'CODE_TAKEN', { code: q.code })
  else codes.add(key)

  if (!nameValid(q.name)) ps.add(`${at}.name`, 'NAME_MISSING')
  if (!LETTER.test(q.letter)) ps.add(`${at}.letter`, 'LETTER_FORMAT')

  const firstOK = q.firstNumber >= MIN_NUMBER && q.firstNumber <= MAX_NUMBER
  const lastOK = q.lastNumber >= MIN_NUMBER && q.lastNumber <= MAX_NUMBER
  if (!firstOK) ps.add(`${at}.firstNumber`, 'RANGE_BOUNDS')
  if (!lastOK) ps.add(`${at}.lastNumber`, 'RANGE_BOUNDS')
  if (firstOK && lastOK && q.lastNumber < q.firstNumber + MIN_SPAN) ps.add(`${at}.lastNumber`, 'RANGE_SHORT')

  if (q.serviceLevelSec < MIN_TARGET || q.serviceLevelSec > MAX_TARGET || q.serviceLevelSec % 60 !== 0) {
    ps.add(`${at}.serviceLevelSec`, 'TARGET_RANGE')
  }
  if (q.type !== 'normal' && q.type !== 'appointment') ps.unknown(`${at}.type`, q.type)
}

// catalog.Name.Validate: at least one language, none blank, codes 2–5 long.
function nameValid(name: Record<string, string> | null | undefined): boolean {
  const entries = Object.entries(name ?? {})
  return entries.length > 0 && entries.every(([lang, text]) => (text ?? '').trim() !== '' && lang.length >= 2 && lang.length <= 5)
}

// Of each overlapping pair sharing a letter the later queue is flagged once, naming
// the other; queues whose range is itself invalid are left out.
function overlaps(ps: Problems, qs: readonly ProfileQueue[]) {
  const byLetter = new Map<string, number[]>()
  qs.forEach((q, i) => {
    if (q.firstNumber >= MIN_NUMBER && q.lastNumber <= MAX_NUMBER && q.firstNumber <= q.lastNumber) {
      byLetter.set(q.letter, [...(byLetter.get(q.letter) ?? []), i])
    }
  })
  const flagged = new Set<number>()
  for (const idx of byLetter.values()) {
    idx.sort((a, b) => qs[a]!.firstNumber - qs[b]!.firstNumber)
    let reach = idx[0]!
    for (const cur of idx.slice(1)) {
      if (qs[cur]!.firstNumber <= qs[reach]!.lastNumber) {
        const later = Math.max(cur, reach), other = Math.min(cur, reach)
        if (!flagged.has(later)) {
          flagged.add(later)
          ps.add(`queues[${later}].firstNumber`, 'RANGES_OVERLAP', { queue: qs[other]!.code })
        }
      }
      if (qs[cur]!.lastNumber > qs[reach]!.lastNumber) reach = cur
    }
  }
}

function work(doc: OperationDoc, ps: Problems) {
  const queues = queueMap(doc)
  const names = new Set<string>()
  doc.workProfiles.forEach((w, i) => workProfile(ps, `workProfiles[${i}]`, w, queues, names))
}

function workProfile(ps: Problems, at: string, w: ProfileWorkProfile, queues: Map<string, ProfileQueue>, names: Set<string>) {
  const name = (w.name ?? '').trim()
  if (name.length > MAX_WORK_NAME) ps.add(`${at}.name`, 'NAME_TOO_LONG', { max: MAX_WORK_NAME })
  else ps.name(`${at}.name`, w.name, names)

  const queueIds = w.queueIds ?? []
  const pools = w.pools ?? []
  const served = new Set<string>()
  queueIds.forEach((q, k) => {
    if (served.has(q)) ps.add(`${at}.queueIds[${k}]`, 'LISTED_TWICE', { value: q })
    else if (!queues.has(q)) ps.add(`${at}.queueIds[${k}]`, 'PROFILE_QUEUE_UNKNOWN')
    served.add(q)
  })
  const drawn = new Set<string>()
  pools.forEach((p, k) => {
    if (drawn.has(p)) ps.add(`${at}.pools[${k}]`, 'LISTED_TWICE', { value: p })
    else if (!POOLS.has(p)) ps.add(`${at}.pools[${k}]`, 'POOL_UNKNOWN', { value: p })
    drawn.add(p)
  })
  if (queueIds.length + pools.length === 0) ps.add(`${at}.queueIds`, 'PROFILE_EMPTY')

  for (const field of ['wrapUpSec', 'noShowSec', 'recallIntervalSec'] as const) {
    if (!whole(w[field], 0, MAX_TIMER)) ps.add(`${at}.${field}`, 'TIMER_RANGE', { min: 0, max: MAX_TIMER })
  }
  if (w.repeatCallSec !== 0 && !whole(w.repeatCallSec, REPEAT_MIN, REPEAT_MAX)) {
    ps.add(`${at}.repeatCallSec`, 'REPEAT_RANGE', { min: REPEAT_MIN, max: REPEAT_MAX })
  }
  const early = w.appointmentEarlySec
  if (early !== undefined && early !== null && !(whole(early, 0, EARLY_MAX) && early % 60 === 0)) {
    ps.add(`${at}.appointmentEarlySec`, 'EARLY_RANGE', { max: EARLY_MAX / 60 })
  }

  const rules = w.rules ?? []
  if (rules.length < 1 || rules.length > LIMITS.rules) ps.add(`${at}.rules`, 'RULES_COUNT', { min: 1, max: LIMITS.rules })
  const ruleIds = new Set<string>()
  rules.forEach((r, k) => rule(ps, `${at}.rules[${k}]`, r, k === 0, served, drawn, queueIds.length + pools.length, ruleIds))
}

/** Mirrors internal/profile/validate_work.go: same codes, paths and args. */
function rule(ps: Problems, at: string, r: CallRule, isFinally: boolean, served: Set<string>, drawn: Set<string>, profileReach: number, ids: Set<string>) {
  if (!r.id) ps.add(`${at}.id`, 'ID_MISSING')
  else if (ids.has(r.id)) ps.add(`${at}.id`, 'RULE_ID_TAKEN', { id: r.id })
  else ids.add(r.id)

  const queueIds = r.queueIds ?? []
  const pools = r.pools ?? []
  const conditions = r.conditions ?? []

  let reach = profileReach
  let ruleQueues = served
  if (isFinally) {
    if (conditions.length > 0) ps.add(`${at}.conditions`, 'FINALLY_CONDITIONS')
    if (queueIds.length > 0) ps.add(`${at}.queueIds`, 'FINALLY_QUEUES')
    if (pools.length > 0) ps.add(`${at}.pools`, 'FINALLY_QUEUES')
  } else {
    if (conditions.length > LIMITS.conditions) ps.add(`${at}.conditions`, 'CONDITIONS_COUNT', { max: LIMITS.conditions })
    ruleQueues = new Set<string>()
    queueIds.forEach((q, k) => {
      if (ruleQueues.has(q)) ps.add(`${at}.queueIds[${k}]`, 'LISTED_TWICE', { value: q })
      else if (!served.has(q)) ps.add(`${at}.queueIds[${k}]`, 'RULE_OUTSIDE_PROFILE')
      ruleQueues.add(q)
    })
    const seen = new Set<string>()
    pools.forEach((p, k) => {
      if (seen.has(p)) ps.add(`${at}.pools[${k}]`, 'LISTED_TWICE', { value: p })
      else if (!drawn.has(p)) ps.add(`${at}.pools[${k}]`, 'RULE_OUTSIDE_PROFILE')
      seen.add(p)
    })
    reach = queueIds.length + pools.length
    if (reach === 0) ps.add(`${at}.queueIds`, 'RULE_EMPTY')
  }

  if (!ORDERS.has(r.order)) ps.unknown(`${at}.order`, r.order)
  else if (r.order === 'single' && reach > 1) ps.add(`${at}.order`, 'SINGLE_NEEDS_ONE')
  if (!PRIMARY.has(r.primaryResource ?? '')) ps.unknown(`${at}.primaryResource`, r.primaryResource ?? '')

  const shares = Object.entries(r.shares ?? {})
  if (shares.length > 0) {
    if (r.order !== 'fair_share') ps.add(`${at}.shares`, 'SHARES_NOT_FAIR')
    else {
      shares.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      for (const [queue, n] of shares) {
        if (!whole(n, MIN_SHARE, MAX_SHARE)) ps.add(`${at}.shares`, 'SHARE_RANGE', { queue, min: MIN_SHARE, max: MAX_SHARE })
        if (!ruleQueues.has(queue)) ps.add(`${at}.shares`, 'SHARE_QUEUE_OUTSIDE', { queue })
      }
    }
  }

  if (isFinally) return // Finally's conditions are refused whole
  conditions.forEach((c, k) => condition(ps, `${at}.conditions[${k}]`, c, served))
  const kinds = new Set(conditions.map((c) => c.kind))
  if ([...kinds].some((k) => COUNTING.has(k)) && [...kinds].some((k) => WAITING.has(k))) {
    ps.add(`${at}.conditions`, 'MIXED_CONDITIONS', undefined, true)
  }
}

function condition(ps: Problems, at: string, c: RuleCondition, served: Set<string>) {
  if (!KINDS.has(c.kind)) { ps.unknown(`${at}.kind`, c.kind); return }
  if (c.kind !== 'time_between' && !served.has(c.queueId ?? '')) ps.add(`${at}.queueId`, 'CONDITION_QUEUE_OUTSIDE')

  const minutes = (lo: number) => {
    if (!whole(c.n, lo, MAX_MINUTES)) ps.add(`${at}.n`, 'MINUTES_RANGE', { min: lo, max: MAX_MINUTES })
  }
  switch (c.kind) {
    case 'wait_over':
      minutes(1)
      break
    case 'over_service_level':
    case 'appointment_late':
      minutes(0)
      break
    case 'visits_count':
    case 'called_count':
      if (!whole(c.n, 0, MAX_COUNT)) ps.add(`${at}.n`, 'COUNT_RANGE', { min: 0, max: MAX_COUNT })
      if (!c.op) ps.add(`${at}.op`, 'OP_MISSING')
      else if (!OPS.has(c.op)) ps.unknown(`${at}.op`, c.op)
      if (c.kind === 'called_count') {
        if (!c.scope) ps.add(`${at}.scope`, 'SCOPE_MISSING')
        else if (!SCOPES.has(c.scope)) ps.unknown(`${at}.scope`, c.scope)
      }
      break
    case 'time_between': {
      const fromOK = whole(c.from, 0, LAST_MINUTE)
      const untilOK = whole(c.until, 0, LAST_MINUTE)
      if (!fromOK) ps.add(`${at}.from`, 'TIME_RANGE', { min: 0, max: LAST_MINUTE })
      if (!untilOK) ps.add(`${at}.until`, 'TIME_RANGE', { min: 0, max: LAST_MINUTE })
      if (fromOK && untilOK && c.from === c.until) ps.add(`${at}.until`, 'TIMES_EQUAL')
      break
    }
  }
}

export function pathLess(a: string, b: string): boolean {
  let i = 0, j = 0
  while (i < a.length && j < b.length) {
    if (isDigit(a[i]!) && isDigit(b[j]!)) {
      let na = 0, nb = 0
      while (i < a.length && isDigit(a[i]!)) na = na * 10 + Number(a[i++])
      while (j < b.length && isDigit(b[j]!)) nb = nb * 10 + Number(b[j++])
      if (na !== nb) return na < nb
      continue
    }
    if (a[i] !== b[j]) return a.charCodeAt(i) < b.charCodeAt(j)
    i++; j++
  }
  return a.length - i < b.length - j
}

const isDigit = (c: string) => c >= '0' && c <= '9'
